import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { debugError } from '@/utils/debugLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    debugError('general', 'ErrorBoundary caught an error:', error, 'ErrorBoundary');
    
    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    this.setState({ errorInfo });
  }

  private handleRefresh = () => {
    // Reset error state and reload the page
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="container py-8">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-xl">Si è verificato un errore</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-muted-foreground">
                Qualcosa è andato storto durante il caricamento di questa pagina.
                Per favore riprova o torna alla pagina precedente.
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 p-4 bg-gray-100 rounded-md">
                  <summary className="cursor-pointer font-medium">
                    Dettagli errore (sviluppo)
                  </summary>
                  <pre className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                    {this.state.error.message}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
              
              <div className="flex justify-center space-x-4">
                <Button onClick={this.handleRefresh} className="flex items-center space-x-2">
                  <RefreshCw className="h-4 w-4" />
                  <span>Ricarica pagina</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.history.back()}
                >
                  Torna indietro
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version per uso più semplice
export function useErrorHandler() {
  return (error: Error, errorInfo?: ErrorInfo) => {
    debugError('general', 'Error caught by handler:', error, 'useErrorHandler');
    
    // In produzione, potresti voler inviare l'errore a un servizio di monitoring
    if (process.env.NODE_ENV === 'production') {
      // Esempio: sendErrorToService(error, errorInfo);
    }
  };
}
